import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Tags,
  Plus,
  MoreVertical,
  Pencil,
  Trash2,
  Loader2,
  FolderOpen,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PartCategories = () => {
  const [categories, setCategories] = useState([]);
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingCategory, setDeletingCategory] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: "",
    description: "",
  });

  const fetchData = async () => {
    try {
      const [categoriesRes, partsRes] = await Promise.all([
        axios.get(`${API}/part-categories`, { withCredentials: true }),
        axios.get(`${API}/parts`, { withCredentials: true }),
      ]);
      setCategories(categoriesRes.data || []);
      setParts(partsRes.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load categories");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const getPartsCount = (categoryId) => {
    return parts.filter(p => p.category_id === categoryId).length;
  };

  const resetForm = () => {
    setFormData({ name: "", description: "" });
  };

  const openCreateDialog = () => {
    resetForm();
    setEditingCategory(null);
    setDialogOpen(true);
  };

  const openEditDialog = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name || "",
      description: category.description || "",
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name) {
      toast.error("Name is required");
      return;
    }
    
    setSaving(true);
    try {
      if (editingCategory) {
        await axios.put(`${API}/part-categories/${editingCategory.category_id}`, formData, { withCredentials: true });
        toast.success("Category updated");
      } else {
        await axios.post(`${API}/part-categories`, formData, { withCredentials: true });
        toast.success("Category created");
      }
      
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save category");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingCategory) return;
    
    try {
      await axios.delete(`${API}/part-categories/${deletingCategory.category_id}`, { withCredentials: true });
      toast.success("Category deleted");
      setDeleteDialogOpen(false);
      setDeletingCategory(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete category");
    }
  };

  if (loading) {
    return (
      <div className="p-6 md:p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-48"></div>
          <div className="h-64 bg-slate-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8" data-testid="part-categories-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900 tracking-tight">Part Categories</h1>
          <p className="text-slate-500 mt-1">Organize your parts into categories</p>
        </div>
        <Button onClick={openCreateDialog} className="rounded-sm bg-slate-900 hover:bg-slate-800">
          <Plus className="h-4 w-4 mr-2" />
          Add Category
        </Button>
      </div>

      {/* Categories Table */}
      <Card className="rounded-sm border-slate-200">
        <CardContent className="p-0">
          {categories.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-center">Parts Count</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {categories.map((category) => {
                  const partsCount = getPartsCount(category.category_id);
                  return (
                    <TableRow 
                      key={category.category_id}
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => openEditDialog(category)}
                      data-testid={`category-row-${category.category_id}`}
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-indigo-100 rounded-sm flex items-center justify-center">
                            <Tags className="h-4 w-4 text-indigo-600" />
                          </div>
                          <span className="font-medium">{category.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-500">
                        {category.description || <span className="text-slate-300">—</span>}
                      </TableCell>
                      <TableCell className="text-center">
                        <span className={partsCount > 0 ? "font-medium" : "text-slate-400"}>
                          {partsCount}
                        </span>
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEditDialog(category)}>
                              <Pencil className="mr-2 h-4 w-4" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => { setDeletingCategory(category); setDeleteDialogOpen(true); }}
                              className="text-red-600"
                              disabled={partsCount > 0}
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-16">
              <FolderOpen className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 text-lg font-medium">No categories yet</p>
              <p className="text-slate-400 text-sm mt-1">Create categories to organize your parts</p>
              <Button onClick={openCreateDialog} variant="outline" className="mt-4 rounded-sm">
                <Plus className="h-4 w-4 mr-2" />
                Add Category
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingCategory ? "Edit Category" : "Add Category"}</DialogTitle>
            <DialogDescription>
              {editingCategory ? "Update category details" : "Create a new category for parts"}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Regulators, Faucets, Labour"
                className="rounded-sm mt-1"
              />
            </div>
            
            <div>
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Optional description..."
                className="rounded-sm mt-1"
                rows={2}
              />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">
                Cancel
              </Button>
              <Button type="submit" disabled={saving} className="rounded-sm">
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingCategory ? "Save Changes" : "Add Category"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Category</DialogTitle>
            <DialogDescription>
              {getPartsCount(deletingCategory?.category_id) > 0 
                ? `Cannot delete "${deletingCategory?.name}" - it has ${getPartsCount(deletingCategory?.category_id)} parts assigned.`
                : `Are you sure you want to delete "${deletingCategory?.name}"?`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} className="rounded-sm">
              Cancel
            </Button>
            {getPartsCount(deletingCategory?.category_id) === 0 && (
              <Button variant="destructive" onClick={handleDelete} className="rounded-sm">
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PartCategories;
